import { makeAutoObservable } from "mobx";
/**
 * Generic FiltersStore using MobX
 * Following the architecture requirements:
 * - MobX for state management instead of useOrderFilters
 * - Configurable filter items
 * - No switch statements
 * - Unified filter functionality
 */
export class FiltersStore {
    constructor() {
        /** Filter items configuration */
        this.items = [];
        /** Current filter values */
        this.values = {};
        /** Loading state for async operations */
        this.isLoading = false;
        /** Error state */
        this.error = null;
        makeAutoObservable(this);
    }
    // === PRIVATE SETTERS ===
    setItems(items) {
        this.items = items;
    }
    setValues(values) {
        this.values = values;
    }
    setValue(name, value) {
        this.values = { ...this.values, [name]: value };
    }
    setIsLoading(isLoading) {
        this.isLoading = isLoading;
    }
    setError(error) {
        this.error = error;
    }
    // === PUBLIC METHODS ===
    /**
     * Set filter items configuration
     * Example: filtersStore.setItems([{ props: { name: 'date', ... }, Cmp: DateRange }])
     */
    setFilterItems(items) {
        // Sort items by order if specified
        const sortedItems = [...items].sort((a, b) => {
            const orderA = a.order ?? 999;
            const orderB = b.order ?? 999;
            return orderA - orderB;
        });
        this.setItems(sortedItems);
    }
    /**
     * Update filter value by name
     */
    updateFilter(name, value) {
        this.setValue(name, value);
    }
    /**
     * Get current filter values
     */
    getFilterValues() {
        return { ...this.values };
    }
    /**
     * Clear specific filter
     */
    clearFilter(name) {
        const newValues = { ...this.values };
        delete newValues[name];
        this.setValues(newValues);
    }
    /**
     * Clear all filters
     */
    clearAllFilters() {
        this.setValues({});
    }
    /**
     * Check if any filters are active
     */
    hasActiveFilters() {
        return Object.keys(this.values).some((key) => {
            const value = this.values[key];
            if (Array.isArray(value)) {
                return value.length > 0;
            }
            return value !== undefined && value !== null && value !== "";
        });
    }
    /**
     * Get count of active filters
     */
    getActiveFilterCount() {
        return Object.keys(this.values).filter((key) => {
            const value = this.values[key];
            if (Array.isArray(value)) {
                return value.length > 0;
            }
            return value !== undefined && value !== null && value !== "";
        }).length;
    }
    /**
     * Get visible filter items
     */
    getVisibleItems() {
        return this.items.filter((item) => item.visible !== false);
    }
    /**
     * Get filter configuration for rendering
     */
    getFilterConfig() {
        return {
            items: this.getVisibleItems(),
            values: this.getFilterValues(),
            onChange: (name, value) => this.updateFilter(name, value),
            onClear: () => this.clearAllFilters(),
        };
    }
    // === PRIVATE METHODS ===
    /**
     * Set loading state with error handling
     */
    async executeWithLoading(operation) {
        try {
            this.setIsLoading(true);
            this.setError(null);
            const result = await operation();
            return result;
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
            this.setError(errorMessage);
            return null;
        }
        finally {
            this.setIsLoading(false);
        }
    }
}
/**
 * Factory function to create a new FiltersStore instance
 */
export const createFiltersStore = () => new FiltersStore();
